import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { Instagram, Youtube } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { prefetchHandlers } from "@/lib/routePrefetch";

// TikTok icon component since it's not in lucide-react
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const socialLinks = [
  { 
    icon: Instagram, 
    href: "https://www.instagram.com/outlaws_sa/?hl=en", 
    label: "Instagram" 
  },
  { 
    icon: Youtube, 
    href: "https://www.youtube.com/@%C3%98UTLAWSSA", 
    label: "YouTube" 
  },
  { 
    icon: TikTokIcon, 
    href: "https://www.tiktok.com/@outlaws_sa3", 
    label: "TikTok" 
  },
];

interface FooterLink {
  name: string;
  href: string;
  protected?: boolean;
  external?: boolean;
}

const shopLinks: FooterLink[] = [
  { name: "Shop (Members)", href: "/shop", protected: true },
  { name: "Apparel", href: "/apparel" },
  { name: "Merchandise", href: "/shop?category=merchandise", protected: true },
  { name: "Accessories", href: "/shop?category=accessories", protected: true },
];

const companyLinks: FooterLink[] = [
  { name: "About Us", href: "/about" },
  { name: "Events", href: "/events", protected: true },
  { name: "Blog", href: "/blog", protected: true },
  { name: "Partners & Sponsors", href: "/partners" },
  { name: "Contact", href: "mailto:support@outlawssa.co.za", external: true },
];

const supportLinks: FooterLink[] = [
  { name: "FAQ", href: "/faq" },
  { name: "Shipping", href: "/policies/delivery" },
  { name: "Returns", href: "/policies/no-refund" },
  { name: "Size Guide", href: "/size-guide" },
];

const policyLinks: FooterLink[] = [
  { name: "Privacy Policy", href: "/policies/privacy" },
  { name: "Terms of Service", href: "/policies/terms" },
  { name: "Outlaws SA Legal Policies", href: "/policies/legal" },
  { name: "No Refund Policy", href: "/policies/no-refund" },
  { name: "License Plate Disclaimer", href: "/policies/license-plate-disclaimer" },
  { name: "Delivery Timeline Policy", href: "/policies/delivery" },
  { name: "Domain & Communication Policy", href: "/policies/domain-communication" },
];

export const Footer = () => {
  const { user } = useAuth();

  const getHref = (link: FooterLink) => {
    if (link.protected && !user) {
      return '/auth';
    }
    return link.href;
  };

  const renderLink = (link: FooterLink) => {
    if (link.external) {
      return (
        <a
          href={link.href}
          className="text-muted-foreground hover:text-foreground transition-colors text-sm"
        >
          {link.name}
        </a>
      );
    }
    return (
      <Link
        to={getHref(link)}
        state={link.protected && !user ? { from: link.href } : undefined}
        {...prefetchHandlers(getHref(link))}
        className="text-muted-foreground hover:text-foreground transition-colors text-sm"
      >
        {link.name}
      </Link>
    );
  };

  return (
    <footer className="bg-secondary border-t border-border/30">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Left Section - Logo & Socials */}
          <div className="space-y-6">
            <Logo className="h-16 w-auto" />
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit Outlaws SA on ${social.label}`}
                  className="w-10 h-10 border border-border/50 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/50 transition-all duration-200"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* SHOP Section */}
          <div>
            <h3 className="font-semibold text-foreground mb-4 tracking-wide">SHOP</h3>
            <ul className="space-y-3">
              {shopLinks.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* COMPANY Section */}
          <div>
            <h3 className="font-semibold text-foreground mb-4 tracking-wide">COMPANY</h3>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* SUPPORT Section */}
          <div>
            <h3 className="font-semibold text-foreground mb-4 tracking-wide">SUPPORT</h3>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Policies Bar */}
      <div className="border-t border-border/30">
        <div className="container mx-auto px-6 py-6">
          <h4 className="font-semibold text-foreground mb-4 tracking-wide text-sm">POLICIES</h4>
          <div className="flex flex-wrap gap-x-6 gap-y-4">
            {policyLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                {...prefetchHandlers(link.href)}
                className="text-muted-foreground hover:text-foreground transition-colors text-sm"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Footer Line */}
      <div className="border-t border-border/30">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 ØUTLAWS. All rights reserved.
            </p>
            <p className="text-sm text-muted-foreground italic">
              All policies are subject to change without prior notice.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};